import { Dialog, Transition } from '@headlessui/react';
import { ArrowPathIcon, GlobeAltIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Fragment, useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { fetchSirenBodacc, resetBodaccData, selectBodaccLoading, selectSirenBodacc } from '../store/slices/bodaccSlice';
import { BODACC_FAMILY_TYPES, formatDate, getBodaccBorderColorClass } from '../utils/bodaccUtils';
import { getBodaccFieldLabel } from '../utils/bodaccDictionary';

interface BodaccDetailProps {
  isOpen: boolean;
  onClose: () => void;
  siren: string;
}

// Define a flexible company type to handle different data structures
interface CompanyInfo {
  denominationUniteLegale?: string;
  sexeUniteLegale?: string;
  prenomUsuelUniteLegale?: string;
  nomUniteLegale?: string;
  commercant?: string;
  name?: string;
  publication?: {
    commercant?: string;
    [key: string]: any;
  };
  [key: string]: any;
}

const BodaccDetail = ({ isOpen, onClose, siren }: BodaccDetailProps) => {
  const dispatch = useDispatch();
  const sirenBodacc = useSelector(selectSirenBodacc);
  const isLoading = useSelector(selectBodaccLoading);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [allEntries, setAllEntries] = useState<any[]>([]);
  const observer = useRef<IntersectionObserver | null>(null);
  const loadingRef = useRef<HTMLDivElement>(null);

  // Safely access company and bodaccEntries with default values
  const company = sirenBodacc?.company as CompanyInfo | null;
  const bodaccEntries = sirenBodacc?.bodaccEntries || [];

  // Log the sirenBodacc state for debugging
  useEffect(() => {
    console.log('sirenBodacc state:', sirenBodacc);
  }, [sirenBodacc]);

  // Reset state when modal is opened
  useEffect(() => {
    if (isOpen && siren) {
      console.log('Modal opened, fetching data for SIREN:', siren);
      setPage(1);
      setHasMore(true);
      setAllEntries([]);
      dispatch(fetchSirenBodacc(siren) as any);
    }

    return () => {
      console.log('Modal closed, resetting data');
      dispatch(resetBodaccData());
    };
  }, [dispatch, isOpen, siren]);

  // Update allEntries when bodaccEntries changes
  useEffect(() => {
    console.log('bodaccEntries changed:', bodaccEntries);
    
    // Handle different possible formats of bodaccEntries
    let entriesToProcess: any[] = [];
    
    if (bodaccEntries) {
      if (Array.isArray(bodaccEntries)) {
        entriesToProcess = bodaccEntries;
      } else if (typeof bodaccEntries === 'object' && bodaccEntries !== null) {
        // If it's an object but not an array, it might be a single entry
        entriesToProcess = [bodaccEntries];
      }
    }
    
    console.log('Entries to process:', entriesToProcess);
    
    if (entriesToProcess.length > 0) {
      console.log('Setting allEntries with new entries');
      setAllEntries(prev => {
        // Filter out duplicates
        const newEntries = entriesToProcess.filter(
          entry => entry && entry.bodacc_id && !prev.some(e => e.bodacc_id === entry.bodacc_id)
        );
        console.log('New entries after filtering duplicates:', newEntries);
        const combined = [...prev, ...newEntries];
        console.log('Combined entries:', combined);
        return combined;
      });
      
      // If we received fewer entries than expected, there are no more to load
      if (entriesToProcess.length < 10) {
        setHasMore(false);
      }
    } else if (page > 1) {
      // If we're past page 1 and got no results, there are no more entries
      setHasMore(false);
    }
  }, [bodaccEntries, page]);

  // Set up intersection observer for infinite loading
  const lastEntryRef = useCallback((node: HTMLDivElement) => {
    if (isLoading) return;
    
    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prevPage => prevPage + 1);
        // Load more data when the last entry is visible
        dispatch(fetchSirenBodacc(`${siren}?page=${page + 1}`) as any);
      }
    });
    
    if (node) observer.current.observe(node);
  }, [isLoading, hasMore, dispatch, siren, page]);

  const getCompanyName = () => {
    if (!company) return siren || '';
    
    // Check if company has a publication property with commercant
    if (company.publication && company.publication.commercant) {
      return company.publication.commercant;
    }
    
    // Check for denominationUniteLegale (from API INSEE)
    if (company.denominationUniteLegale) {
      return company.denominationUniteLegale;
    }
    
    // Check for person name format (from API INSEE)
    if (company.sexeUniteLegale && company.prenomUsuelUniteLegale && company.nomUniteLegale) {
      return `${company.sexeUniteLegale === 'M' ? 'MONSIEUR' : 'MADAME'} ${company.prenomUsuelUniteLegale} ${company.nomUniteLegale}`;
    }
    
    // Check for commercant property directly
    if (company.commercant) {
      return company.commercant;
    }
    
    // Check for name property
    if (company.name) {
      return company.name;
    }
    
    // Fallback to SIREN
    return siren || '';
  };

  // Render a nested object with proper labels
  const renderNestedObject = (obj: any, depth = 0) => {
    if (!obj || typeof obj !== 'object') return null;
    
    return Object.entries(obj)
      .filter(([key, value]) => 
        value !== null && 
        value !== undefined && 
        value !== '' && 
        key !== 'id' && 
        key !== 'bodacc_id' &&
        key !== 'publication'
      )
      .map(([key, value], index) => {
        const label = getBodaccFieldLabel(key);
        const uniqueKey = `${key}-${index}-${depth}-${typeof value === 'object' ? JSON.stringify(value).substring(0, 20) : value}`;
        
        if (typeof value === 'object' && value !== null) {
          return (
            <div key={uniqueKey} className="mt-2">
              <p className="font-semibold">{label}:</p>
              <div className="ml-4">
                {renderNestedObject(value, depth + 1)}
              </div>
            </div>
          );
        }
        
        if (key === 'dateparution' || key.includes('date')) {
          return (
            <div key={uniqueKey} className="mt-1">
              <span className="font-medium">{label}: </span>
              <span>{formatDate(value as string)}</span>
            </div>
          );
        }
        
        return (
          <div key={uniqueKey} className="mt-1">
            <span className="font-medium">{label}: </span>
            <span>{value as string}</span>
          </div>
        );
      });
  };

  // Helper function to safely parse JSON
  const safelyParseJSON = (jsonString: string) => {
    try {
      return JSON.parse(jsonString);
    } catch (error) {
      console.error('Error parsing JSON:', error);
      return jsonString;
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="div"
                  className="flex justify-between items-center border-b pb-3 mb-4"
                >
                  <h3 className="text-lg font-medium leading-6 text-gray-900">
                    Détails BODACC - {getCompanyName()}
                  </h3>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-500"
                    onClick={onClose}
                  >
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </Dialog.Title>

                {isLoading && page === 1 ? (
                  <div className="flex justify-center items-center h-40">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                  </div>
                ) : (
                  <div className="overflow-y-auto max-h-[70vh]">
                    {/* Debug log */}
                    {(() => { 
                      console.log('Rendering with allEntries:', allEntries); 
                      console.log('isLoading:', isLoading, 'page:', page);
                      return null; 
                    })()}
                    
                    {(!isLoading && allEntries.length === 0 && page === 1) ? (
                      <div className="text-center py-8">
                        <p className="text-gray-500">Aucune publication BODACC trouvée pour ce SIREN.</p>
                      </div>
                    ) : (
                      <>
                        <a
                          className="inline-block rounded-md bg-indigo-600 text-white text-sm px-4 py-2 mb-4 hover:bg-indigo-700"
                          target="_blank"
                          rel="noopener noreferrer"
                          href={`https://www.bodacc.fr/pages/annonces-commerciales/?q.registre=registre:${siren}`}
                        >
                          <GlobeAltIcon className="h-4 w-4 inline mr-1" />
                          Voir sur le site BODACC
                        </a>

                        {company && (
                          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                            <h4 className="font-medium text-gray-700 mb-2">Informations de l'entreprise</h4>
                            {renderNestedObject(company)}
                          </div>
                        )}

                        <div className="space-y-4">
                          {allEntries.map((entry, idx) => {
                            // Skip entries without required properties
                            if (!entry || !entry.publication) {
                              console.log('Skipping invalid entry:', entry);
                              return null;
                            }
                            
                            const familyType = entry.publication.familleavis;
                            const familyLabel = BODACC_FAMILY_TYPES[familyType as keyof typeof BODACC_FAMILY_TYPES] || familyType;
                            const borderColorClass = getBodaccBorderColorClass(familyType);
                            const isLastEntry = idx === allEntries.length - 1;

                            return (
                              <div
                                key={`bodacc-entry-${entry.bodacc_id}-${idx}`}
                                className={`p-4 border rounded-lg border-l-4 ${borderColorClass}`}
                                ref={isLastEntry ? lastEntryRef : undefined}
                              >
                                <h3 className="font-bold">
                                  {entry.publication.typeavis_lib || 'Jugement'} {familyLabel}
                                </h3>
                                <div className="mt-2 space-y-1 text-sm">
                                  <p>
                                    Annonce {entry.publication.publicationavis}/N°{entry.publication.numeroannonce} publiée le {formatDate(entry.publication.dateparution)}
                                  </p>
                                  <p>
                                    SIREN : {entry.siren}
                                    {entry.publication.ville && ` - ${entry.publication.ville}`}
                                    {entry.publication.numerodepartement && ` - Département : ${entry.publication.numerodepartement}`}
                                  </p>
                                  
                                  {entry.publication.jugement && (
                                    <div className="mt-2">
                                      <p className="font-semibold">Jugement :</p>
                                      <p className="whitespace-pre-line">
                                        {typeof entry.publication.jugement === 'string' && entry.publication.jugement.startsWith('{') 
                                          ? renderNestedObject(safelyParseJSON(entry.publication.jugement))
                                          : entry.publication.jugement}
                                      </p>
                                    </div>
                                  )}
                                  
                                  {entry.publication.acte && (
                                    <div className="mt-2">
                                      <p className="font-semibold">Acte :</p>
                                      <p className="whitespace-pre-line">
                                        {typeof entry.publication.acte === 'string' && entry.publication.acte.startsWith('{') 
                                          ? renderNestedObject(safelyParseJSON(entry.publication.acte))
                                          : entry.publication.acte}
                                      </p>
                                    </div>
                                  )}
                                  
                                  {entry.publication.modificationsgenerales && (
                                    <div className="mt-2">
                                      <p className="font-semibold">Modifications :</p>
                                      <p className="whitespace-pre-line">
                                        {typeof entry.publication.modificationsgenerales === 'string' && entry.publication.modificationsgenerales.startsWith('{') 
                                          ? renderNestedObject(safelyParseJSON(entry.publication.modificationsgenerales))
                                          : entry.publication.modificationsgenerales}
                                      </p>
                                    </div>
                                  )}

                                  {entry.publication.listepersonnes && (
                                    <div className="mt-2">
                                      <p className="font-semibold">Personnes :</p>
                                      <div className="ml-2">
                                        {typeof entry.publication.listepersonnes === 'string' && entry.publication.listepersonnes.startsWith('{') 
                                          ? renderNestedObject(safelyParseJSON(entry.publication.listepersonnes))
                                          : entry.publication.listepersonnes}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {entry.publication.depot && (
                                    <div className="mt-2">
                                      <p className="font-semibold">Dépôt :</p>
                                      <div className="ml-2">
                                        {typeof entry.publication.depot === 'string' && entry.publication.depot.startsWith('{') 
                                          ? renderNestedObject(safelyParseJSON(entry.publication.depot))
                                          : entry.publication.depot}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {entry.publication.radiationaurcs && (
                                    <div className="mt-2">
                                      <p className="font-semibold">Radiation :</p>
                                      <div className="ml-2">
                                        {typeof entry.publication.radiationaurcs === 'string' && entry.publication.radiationaurcs.startsWith('{') 
                                          ? renderNestedObject(safelyParseJSON(entry.publication.radiationaurcs))
                                          : entry.publication.radiationaurcs}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        
                        {/* Loading indicator for infinite scroll */}
                        {isLoading && page > 1 && (
                          <div className="flex justify-center items-center py-4" ref={loadingRef}>
                            <ArrowPathIcon className="h-6 w-6 animate-spin text-indigo-600" />
                          </div>
                        )}
                        
                        {/* End of results message */}
                        {!hasMore && allEntries.length > 0 && (
                          <div className="text-center py-4 text-gray-500">
                            Fin des résultats
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default BodaccDetail; 